import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { EventSchedulePriceGrade, LocCDType, ShowTixOrderResponse } from 'api';
import dayjs from 'dayjs';
import {
  addDaysToDate,
  areTheseDatesEqual,
  convertDateTimeToUTC,
  getEndOfWeek,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getStartOfWeek,
  getStencilDate,
  isTheSameMonth,
  isThisDateAPastDate,
} from 'common';
import { months } from '../months';
import {
  CalendarDayEvent,
  CalendarEventTab,
  DayEvent,
  EventDetail,
} from '../../interfaces/calendar.interfaces';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { HoverStateDirective } from 'ticketing/hover-state-component/hover-state.directive';
import { SnackBarModule, SnackBarService } from 'ui-elements/snack-bar';
import { CheckboxComponent } from 'ui-elements/form/checkbox';
import {
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  CalendarDateSelectionModel,
  CalendarDateShowSelectionModel,
  CalendarDatesSelectionModel,
} from './calendar-grid.component.types';

@Component({
  selector: 'ss-stx-shared-calendar-grid',
  templateUrl: './calendar-grid.component.html',
  styleUrls: ['./calendar-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    HoverStateDirective,
    CheckboxComponent,
    FormsModule,
    ReactiveFormsModule,
    SnackBarModule,
  ],
})
export class CalendarGridComponent implements OnChanges {
  private snack = inject(SnackBarService);

  @Input() locationSelected?: LocCDType | null;
  @Input() dayEvents?: DayEvent[];
  @Input() eventCompTypeSelected?: string;
  @Input() activeDate: Date = new Date();
  @Input() selectedEventType: CalendarEventTab = {
    label: 'ALL',
    disabled: false,
    open: true,
  };
  @Input() order?: ShowTixOrderResponse;
  @Input() hideHoverPreview?: boolean;
  @Input() selectableDates?: boolean;
  @Input() dateRanges?: {
    startDate: Date;
    endDate: Date;
  }[];

  @Output() monthChange = new EventEmitter<number>();
  @Output() slotTimeClick = new EventEmitter<EventDetail>();
  @Output() calendarDateChecked = new EventEmitter<{
    calendarData: CalendarDayEvent[][];
    dayEvent: CalendarDayEvent;
    checked: boolean;
  }>();
  @Output() calendarDateShowChecked = new EventEmitter<{
    calendarData: CalendarDayEvent[][];
    dayEvent: CalendarDayEvent;
    eventDate: Date;
    checked: boolean;
  }>();

  months = months;

  allWeeks: CalendarDayEvent[][] = [];
  selectedMonth: string = '';

  today = new Date();
  form: FormGroup<CalendarDatesSelectionModel>;

  constructor() {
    this.form = new FormGroup<CalendarDatesSelectionModel>({
      dates: new FormArray<FormGroup<CalendarDateSelectionModel>>([]),
    });
  }

  //#region Lifecycle hooks
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['dayEvents']?.currentValue ||
      (this.selectableDates && changes['dateRanges']?.currentValue)
    ) {
      this.allWeeks = this.getDates();
    }

    if (changes['activeDate']?.currentValue) {
      this.selectedMonth = this.months[this.activeDate.getMonth()];
    }
  }
  //#endregion

  // #region Private methods
  private generateDatesForCurrentWeek(date: Date) {
    let currentDate = new Date(date);
    const week: CalendarDayEvent[] = [];

    for (let day = 0; day < 7; day++) {
      const formEvtDate = new Date(getStencilDate(currentDate));
      const ctrl = new FormGroup<CalendarDateSelectionModel>({
        date: new FormControl(formEvtDate),
        selected: new FormControl(this.isDateSelected(formEvtDate)),
        shows: new FormArray<FormGroup<CalendarDateShowSelectionModel>>([]),
      });

      this.form.controls.dates.push(ctrl);

      if (ctrl.getRawValue().selected) {
        ctrl.controls.selected.disable();
      }
      ctrl.updateValueAndValidity();

      const dayEvent = this.dayEvents?.find((event) => {
        return areTheseDatesEqual(currentDate, event.date);
      });

      if (dayEvent) {
        // verify if each individual event is a past date
        dayEvent.eventsDetail.forEach((event) => {
          const formEvtShowDate = convertDateTimeToUTC(
            event.eventDate as any as string
          );
          const dayFormGroup = new FormGroup<CalendarDateShowSelectionModel>({
            date: new FormControl(formEvtShowDate),
            selected: new FormControl(this.isDateSelected(formEvtShowDate)),
          });
          ctrl.controls.shows.push(dayFormGroup);
          if (dayFormGroup.getRawValue().selected) {
            dayFormGroup.controls.selected.disable();
          }
          dayFormGroup.updateValueAndValidity();

          event.isPastDate = isThisDateAPastDate(
            this.today,
            event.eventDate,
            undefined,
            this.locationSelected === 'BRANSON_MO'
              ? 'America/Chicago'
              : 'America/New_York'
          );
          event.inCart =
            this.order &&
            this.order?.activeShowTourOrderItems.findIndex(
              (item) =>
                item.eventDateTime === event.eventDate &&
                event.eventType === item.itemType
            ) !== -1;
        });

        week.push({
          ...dayEvent,
          currentMonth: isTheSameMonth(currentDate, dayEvent.date),
        });
      } else {
        week.push({
          date: currentDate,
          currentMonth: isTheSameMonth(currentDate, this.activeDate),
        });
      }
      currentDate = addDaysToDate(currentDate, 1);
    }
    return week;
  }

  private getDates() {
    const startOfTheSelectedMonth = getFirstDayOfMonth(this.activeDate);
    const endOfTheSelectedMonth = getLastDayOfMonth(this.activeDate);
    const startDate = getStartOfWeek(startOfTheSelectedMonth);
    const endDate = getEndOfWeek(endOfTheSelectedMonth);

    let currentDate = startDate;

    const allWeeks: CalendarDayEvent[][] = [];

    this.form.controls.dates.clear();

    while (currentDate <= endDate) {
      allWeeks.push(this.generateDatesForCurrentWeek(currentDate));
      currentDate = addDaysToDate(currentDate, 7);
    }

    return allWeeks;
  }
  //#endregion

  //#region public methods
  handleMonthClick(monthIndex: number) {
    this.monthChange.emit(monthIndex);
  }

  handleEventSlotTimeClick(detail: EventDetail) {
    if (detail.inCart) {
      return;
    }

    this.slotTimeClick.emit(detail);
  }

  getPrettyDate(date: Date): string {
    return dayjs(date).format('dddd MMM D, YYYY').toString();
  }

  getPrettyTime(date: Date): string {
    return dayjs(date).format('h:mm a').toString();
  }

  getPriceGrade(priceGrades: EventSchedulePriceGrade[]) {
    return priceGrades.map((pg) => ({
      priceGrade: pg.priceGrade.description,
      seatCount: pg.availableSeats,
    }));
  }

  onCalendarDateChecked(
    checkedDetail: { detail: boolean },
    dayEvent: CalendarDayEvent
  ) {
    this.calendarDateChecked.emit({
      calendarData: this.allWeeks,
      dayEvent,
      checked: checkedDetail.detail,
    });
  }

  onCalendarDateShowChecked(
    checkedDetail: { detail: boolean },
    dayEvent: CalendarDayEvent,
    eventDate: Date
  ) {
    this.calendarDateShowChecked.emit({
      calendarData: this.allWeeks,
      dayEvent,
      eventDate,
      checked: checkedDetail.detail,
    });
  }

  tryClickOnDisabledMode() {
    this.snack.show({ message: 'Please uncheck via date range selections' });
  }

  private isDateSelected(date: Date) {
    if (!this.dateRanges || !this.selectableDates) {
      return false;
    }

    return this.dateRanges.some(
      (range) =>
        date.getTime() >= Number(range.startDate) &&
        date.getTime() <= Number(range.endDate)
    );
  }
}
